import { defineStore } from "pinia";
import { hash } from "ohash";
import { useOrgStore } from "./org";
import { useUserStore } from "./user";
import { useSearchStore } from "./search";
import type {
  AlertItem,
  DigestItem,
  DigestSectionItem,
} from "~/types/AlertStateType";
import type AlertState from "~/types/AlertStateType";
import type RequestOptions from "~/types/RequestOptionsType";
import type RailsApiObject from "~/types/RailsApiObjectType";
import { useApiCall, invalidateApiCache } from "~/composables/useApiCall";

type RecipientObject = RailsApiObject<Record<string, unknown>>;

export const useAlertStore = defineStore("alerts", {
  state: (): AlertState => ({
    loading: false,
    sortBy: 1,
    orderBy: 1,
    page: 1,
    total: 0,
    totalRealTimeAlertsCount: 0,
    totalDigestAlertsCount: 0,
    perPage: 10,
    searchText: "",
    digests: [],
    digestSections: [],
    newDigestSections: [],
    removedDigestSections: [],
    originalDigestSections: [],
    originalDigestSectionHashMap: new Map(),
    digestRecipientsMap: new Map(),
    alerts: [],
    alertRecipientsMap: new Map(),
    selectedAlert: null,
    selectedAlertCopy: null,
  }),
  getters: {
    alertConditionType: (state) => {
      if (state?.selectedAlert?.metadata.alertType === "real-time") {
        return state.selectedAlert?.attributes.search_category;
      }
      return "";
    },
    alertEventType: (state) => {
      if (state?.selectedAlert?.metadata.alertType === "real-time") {
        return state.selectedAlert?.attributes.event_type;
      }
      return "";
    },
    alertSearchText: (state) => {
      if (state?.selectedAlert?.metadata.alertType === "real-time") {
        return state.selectedAlert?.attributes.alert_search_text;
      }
      return "";
    },
    isRealTimeAlert(state) {
      return state?.selectedAlert?.metadata.alertType === "real-time";
    },
    isDigest(state) {
      return state?.selectedAlert?.metadata.alertType === "digest";
    },
    pageStart: (state) => (state.page - 1) * state.perPage + 1,
    pageEnd: (state) =>
      state.page * state.perPage > state.total
        ? state.total
        : state.page * state.perPage,
    sortMap: () => {
      return { 1: "created_at", 2: "name" };
    },
    orderByModifier: (state) => {
      if (state.sortBy === state.orderBy) {
        return "-";
      }
      return "";
    },
    defaultRealTimeAlert: () => (t: (key: string) => string) => {
      const orgStore = useOrgStore();
      const userStore = useUserStore();
      return {
        attributes: {
          name: "",
          trigger_type: "search_dashboard",
          frequency: "asap",
          send_content_unlocked: false,
          alert_search_text: t(
            "alertTypes.preconfiguredQueries.ipoAndFundraising",
          ),
          time_zone: orgStore.getTimeZone,
          send_hour: "00:00",
          created_at: null,
          status: "active",
          filter_languages: "",
          filter_article_sentiment: "positive|neutral|negative",
          filter_countries: "",
          delivery_mode: "email",
          search_category: "everything",
          event_type: "ipo_and_fundraising",
        },
        relationships: {
          profile: {},
          search_dashboard: {},
          search_filter: {},
          user: {
            data: {
              type: "users",
              id: userStore.getUser?.id,
            },
          },
          organization: {
            data: {
              type: "organizations",
              id: orgStore.getOrg?.id,
            },
          },
        },
        type: "article-alerts",
        metadata: {
          alertType: "real-time",
          isUnsaved: true,
        },
      };
    },
    defaultDigest: () => {
      const orgStore = useOrgStore();
      const userStore = useUserStore();
      return {
        attributes: {
          name: "",
          status: "active",
          frequency: "daily",
          send_if_empty: false,
          send_content_unlocked: false,
          send_hour: "0:00",
          created_at: null,
          time_zone: orgStore.getTimeZone,
        },
        relationships: {
          profile: {
            data: {
              type: "profiles",
              id: "",
            },
          },
          search_dashboard: {
            data: {
              type: "search_dashboards",
              id: "",
            },
          },
          user: {
            data: {
              type: "users",
              id: userStore.getUser?.id ?? "",
            },
          },
          organization: {
            data: {
              type: "organizations",
              id: orgStore.getOrg?.id ?? "",
            },
          },
        },
        type: "email-digests",
        metadata: {
          alertType: "digest",
          isUnsaved: true,
        },
      };
    },
    unsavedAlertSelected: (state) => {
      return state.selectedAlert?.metadata?.isUnsaved;
    },
    updatedDigestSections: (state) => {
      const { digestSections, originalDigestSectionHashMap } = state;

      return digestSections.filter((section) => {
        const originalHash = originalDigestSectionHashMap.get(section.id);
        return originalHash && originalHash !== hash(section);
      });
    },
    dirtyForm: (state) => {
      // NOTE: Recipients are compared outside this getter at the component level
      if (state.selectedAlert && state.selectedAlertCopy) {
        if (state?.selectedAlert?.metadata?.alertType === "real-time") {
          return (
            state.selectedAlert?.attributes?.name !==
              state.selectedAlertCopy?.attributes?.name ||
            state.selectedAlert?.relationships?.profile?.data?.id !==
              state.selectedAlertCopy?.relationships?.profile?.data?.id ||
            state.selectedAlert?.attributes?.search_category !==
              state.selectedAlertCopy?.attributes?.search_category ||
            state.selectedAlert?.attributes?.event_type !==
              state.selectedAlertCopy?.attributes?.event_type ||
            state.selectedAlert?.attributes?.alert_search_text !==
              state.selectedAlertCopy?.attributes?.alert_search_text ||
            state.selectedAlert?.attributes?.filter_article_sentiment !==
              state.selectedAlertCopy?.attributes?.filter_article_sentiment ||
            state.selectedAlert?.attributes?.send_content_unlocked !==
              state.selectedAlertCopy?.attributes?.send_content_unlocked
          );
        } else if (state?.selectedAlert?.metadata?.alertType === "digest") {
          const areDigestsDiff =
            JSON.stringify(state.digestSections) !==
            JSON.stringify(state.originalDigestSections);

          return (
            state.selectedAlert?.attributes?.name !==
              state.selectedAlertCopy?.attributes?.name ||
            state.selectedAlert?.attributes?.frequency !==
              state.selectedAlertCopy?.attributes?.frequency ||
            state.selectedAlert?.attributes?.send_hour !==
              state.selectedAlertCopy?.attributes?.send_hour ||
            state.selectedAlert?.attributes?.send_if_empty !==
              state.selectedAlertCopy?.attributes?.send_if_empty ||
            state.selectedAlert?.attributes?.send_content_unlocked !==
              state.selectedAlertCopy?.attributes?.send_content_unlocked ||
            areDigestsDiff
          );
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
  },
  actions: {
    addDigestSection() {
      let id = "0";
      while (
        id === "0" ||
        this.digestSections?.find((rec: DigestSectionItem) => rec.id === id)
      ) {
        id = String(Math.floor(Math.random() * 1000000));
      }
      const newSection: DigestSectionItem = {
        id,
        attributes: {
          name: "",
          sort_option: "relevance:desc",
          articles_count: 10,
        },
        relationships: {
          profile: {
            data: null,
          },
          search_dashboard: {
            data: null,
          },
          search_filter: {
            data: null,
          },
        },
        type: "email_digest_section",
      };

      this.digestSections.push(newSection);
      this.newDigestSections.push(newSection);
    },
    removeDigestSection(id: string) {
      this.digestSections = this.digestSections?.filter(
        (digestSection: DigestSectionItem) => digestSection.id !== id,
      );
      if (
        this.originalDigestSections.some(
          (section: DigestSectionItem) => String(section.id) === String(id),
        )
      ) {
        this.removedDigestSections.push(String(id));
      }

      if (
        this.newDigestSections.some(
          (section: DigestSectionItem) => String(section.id) === String(id),
        )
      ) {
        this.newDigestSections = this.newDigestSections.filter(
          (digestSection: DigestSectionItem) => digestSection.id !== id,
        );
      }
    },
    beginUnsavedAlert(alertType: string, t: (key: string) => string) {
      // Determine which object to spread based on the alertType
      // Deep copy to prevent mutation
      let alertAttributes;
      let alertRelationships;

      if (alertType === "digest") {
        alertAttributes = JSON.parse(
          JSON.stringify(this.defaultDigest.attributes),
        );
        alertRelationships = JSON.parse(
          JSON.stringify(this.defaultDigest.relationships),
        );
      } else {
        // Execute the getter function with 't'
        const defaultAlert = this.defaultRealTimeAlert(t);
        alertAttributes = JSON.parse(JSON.stringify(defaultAlert.attributes));
        alertRelationships = JSON.parse(
          JSON.stringify(defaultAlert.relationships),
        );
      }

      this.selectedAlert = {
        id: "-1",
        attributes: {
          ...alertAttributes,
        },
        relationships: {
          ...alertRelationships,
        },
        type:
          alertType === "digest" ? this.defaultDigest.type : "article-alerts", // Hardcoded or accessed from defaultAlert
        metadata: {
          alertType,
          isUnsaved: true,
        },
      };
      this.selectedAlertCopy = JSON.parse(JSON.stringify(this.selectedAlert));
    },
    getQueryString() {
      return `?filter[name_cont_any]=${this.searchText}&sort=${this.orderByModifier}${this.sortMap[this.sortBy]}&page[number]=${this.page}&page[size]=${this.perPage}`;
    },
    async updateActiveTab(tabName: string) {
      this.page = 1;
      this.sortBy = 1;
      this.orderBy = 1;
      this.searchText = "";
      await this.fetchPage(tabName);
    },
    updateSearchText(newText: string, tabName: string) {
      this.searchText = newText;
      this.page = 1;
      this.fetchPage(tabName);
    },
    handleSortChange(optionsArr: Array<number>, tabName: string) {
      this.sortBy = optionsArr[0] as 1 | 2;
      this.orderBy = optionsArr[1] as 1 | 2;
      this.fetchPage(tabName);
    },
    handlePaginateForward(tabName: string) {
      if (this.page * this.perPage < this.total) {
        this.page += 1;
        this.fetchPage(tabName);
      }
    },
    handlePaginateBackward(tabName: string) {
      if (this.pageStart !== 1) {
        this.page -= 1;
        this.fetchPage(tabName);
      }
    },
    mapDigestRecipients(recipients: Array<RecipientObject>) {
      this.digestRecipientsMap = new Map();
      recipients.forEach((item: RecipientObject) => {
        const alertId = item?.relationships?.email_digest?.data?.id || 0;
        if (alertId) {
          if (this.digestRecipientsMap.has(alertId)) {
            this.digestRecipientsMap.get(alertId).push(item);
          } else {
            this.digestRecipientsMap.set(alertId, [item]);
          }
        }
      });
    },
    mapAlertRecipients(recipients: Array<RecipientObject>) {
      this.alertRecipientsMap = new Map();
      recipients.forEach((item: RecipientObject) => {
        const alertId = item?.relationships?.article_alert?.data?.id || 0;
        if (alertId) {
          if (this.alertRecipientsMap.has(alertId)) {
            this.alertRecipientsMap.get(alertId).push(item);
          } else {
            this.alertRecipientsMap.set(alertId, [item]);
          }
        }
      });
    },

    clearData() {
      const searchStore = useSearchStore();
      searchStore.mentions = [];
      searchStore.selectedSearch = {};
      searchStore.selectedNestedSearch = {};
      this.selectedAlert = null;
      this.selectedAlertCopy = null;
      this.digestSections = [];
      this.newDigestSections = [];
      this.removedDigestSections = [];
      this.originalDigestSections = [];
      this.originalDigestSectionHashMap = new Map();
    },

    async updateAlert(data: AlertItem) {
      const { created_at, updated_at, ...attributes } = data.attributes;
      const payload = {
        ...data,
        attributes,
      };

      const options: RequestOptions = {
        method: "PATCH",
        body: { data: payload },
      };

      const idx = this.alerts.findIndex((alert) => alert.id === data.id);
      this.alerts[idx] = data;

      try {
        const res = await useApiCall(`/article_alerts/${data.id}`, options);
        const updatedItem = res.data._rawValue?.data;
        if (updatedItem && idx !== -1) {
          this.alerts[idx] = updatedItem;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    async updateDigest(data: DigestItem) {
      const { created_at, updated_at, ...attributes } = data.attributes;
      const payload = {
        ...data,
        attributes,
      };

      const options: RequestOptions = {
        method: "PATCH",
        body: { data: payload },
      };

      const idx = this.digests.findIndex((digest) => digest.id === data.id);
      this.digests[idx] = data;

      try {
        const res = await useApiCall(`/email_digests/${data.id}`, options);
        const updatedItem = res.data._rawValue?.data;
        if (updatedItem && idx !== -1) {
          this.digests[idx] = updatedItem;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    async fetchPage(tabName: string) {
      if (tabName === "digest") {
        await this.fetchDigests();
      } else {
        await this.fetchAlerts();
      }
    },
    async fetchAlerts() {
      try {
        const res = await useApiCall(`/article_alerts${this.getQueryString()}`);

        this.alerts = res.data._rawValue.data;
        this.total = res.data._rawValue.meta.total_records;
        if (!this.searchText) {
          this.totalRealTimeAlertsCount = res.data._rawValue.meta.total_records;
        }
        this.mapAlertRecipients(res?.data?._rawValue?.included || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    async fetchDigests() {
      try {
        const res = await useApiCall(`/email_digests${this.getQueryString()}`);

        this.digests = res.data._rawValue.data;
        this.total = res.data._rawValue.meta.total_records;
        if (!this.searchText) {
          this.totalDigestAlertsCount = res.data._rawValue.meta.total_records;
        }
        this.mapDigestRecipients(res?.data?._rawValue?.included || []);
        await this.fetchDigestSections();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },

    async deleteAlert(id: string) {
      const options: RequestOptions = {
        method: "DELETE",
      };
      try {
        await useApiCall(`/article_alerts/${id}`, options);

        this.alerts = this.alerts.filter((alert) => alert.id !== id);
        this.total -= 1;
        this.totalRealTimeAlertsCount = Math.max(
          0,
          this.totalRealTimeAlertsCount - 1,
        );

        invalidateApiCache("/article_alerts");
        await this.fetchAlerts();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },

    async deleteDigest(id: string) {
      const options: RequestOptions = {
        method: "DELETE",
      };
      try {
        await useApiCall(`/email_digests/${id}`, options);

        this.digests = this.digests.filter((digest) => digest.id !== id);
        this.total -= 1;
        this.totalDigestAlertsCount = Math.max(
          0,
          this.totalDigestAlertsCount - 1,
        );

        invalidateApiCache("/email_digests");
        await this.fetchDigests();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },

    async fetchDigestSections() {
      const setOriginalDigestSections = () => {
        this.originalDigestSections = JSON.parse(
          JSON.stringify(this.digestSections),
        );
      };
      if (this.selectedAlert) {
        if (this.unsavedAlertSelected) {
          // Not a saved alert, therefore no sections to fetch
          // Just set the original sections property and get outta here
          return setOriginalDigestSections();
        }
        const options: RequestOptions = {
          method: "GET",
        };
        try {
          const resp = await useApiCall(
            `/email_digest_sections?email_digest_id=${this.selectedAlert.id}`,
            options,
          );
          this.digestSections = resp.data?._rawValue?.data;
          if (this.digestSections) {
            setOriginalDigestSections();
          }
          this.originalDigestSections.forEach((section) => {
            this.originalDigestSectionHashMap.set(section.id, hash(section));
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      }
    },

    async createOrUpdateAlert() {
      const method = this.unsavedAlertSelected ? "POST" : "PATCH";
      let route =
        this.selectedAlert?.metadata.alertType === "digest"
          ? "/email_digests"
          : "/article_alerts";

      if (method === "PATCH") {
        route = `${route}/${this.selectedAlert?.id}`;
      }

      const { created_at, updated_at, ...alertAttributes } = {
        ...this.selectedAlert?.attributes,
      };
      const body = {
        data: {
          attributes: alertAttributes,
          relationships: { ...this.selectedAlert?.relationships },
          type: this.selectedAlert?.type,
        },
      };

      if (this.selectedAlert?.metadata.alertType === "digest") {
        delete body.data.attributes.test_date_end;
        delete body.data.attributes.test_date_start;

        if (this.unsavedAlertSelected) {
          body.data.relationships.search_dashboard = {
            data: {
              id: Array.isArray(
                this.newDigestSections[0].relationships.search_dashboard.data,
              )
                ? this.newDigestSections[0].relationships.search_dashboard
                    .data[0]?.id
                : this.newDigestSections[0].relationships.search_dashboard.data
                    ?.id,
              type: "search-dashboards",
            },
          };
          const profileData =
            this.newDigestSections[0].relationships.profile.data;
          body.data.relationships.profile = {
            data: {
              id: Array.isArray(profileData)
                ? profileData[0]?.id
                : profileData?.id,
              type: "profiles",
            },
          };
        }
      } else if (this.unsavedAlertSelected) {
        body.data.relationships.search_dashboard = {
          data: {
            id: this.selectedAlert?.relationships?.search_dashboard?.data?.id,
            type: "search-dashboards",
          },
        };
      }

      const options: RequestOptions = {
        method,
        body,
      };

      const resp = await useApiCall(route, options);

      if (!resp.error?.value) {
        // ✅ Invalidate caches only on success
        if (this.selectedAlert?.metadata.alertType === "digest") {
          invalidateApiCache("/email_digests");
        } else {
          invalidateApiCache("/article_alerts");
        }
      }

      if (this.selectedAlert) {
        this.selectedAlert.id = resp.data?._rawValue?.data?.id;
      }
      return resp;
    },

    async addNewRecipient(newData: RecipientObject) {
      const route =
        this.selectedAlert?.metadata.alertType === "digest"
          ? "/email_digest_recipients"
          : "/article_alert_subscribers";

      const body = {
        data: {
          attributes: { ...newData.attributes },
          relationships: { ...newData.relationships },
          type: newData.type,
        },
      };

      const options: RequestOptions = {
        method: "POST",
        body,
      };

      await useApiCall(route, options);
    },

    async removeRecipient(id: string) {
      const route =
        this.selectedAlert?.metadata.alertType === "digest"
          ? `/email_digest_recipients/${id}`
          : `/article_alert_subscribers/${id}`;

      const options: RequestOptions = {
        method: "DELETE",
      };

      await useApiCall(route, options);
    },

    async createOrUpdateDigestSection(
      sectionData: DigestSectionItem,
      method: "POST" | "PATCH",
    ) {
      const body = {
        data: {
          attributes: { ...sectionData.attributes },
          relationships: { ...sectionData.relationships },
          type: "email-digest-sections",
        },
      };
      const options: RequestOptions = {
        method,
        body,
      };
      const route =
        method === "PATCH"
          ? `/email_digest_sections/${sectionData.id}`
          : "/email_digest_sections";

      await useApiCall(route, options);
      // Invalidate cache after creation or update
      invalidateApiCache("/email_digest_sections");
    },

    async removeOldDigestSection(id: string) {
      const options: RequestOptions = {
        method: "DELETE",
      };

      await useApiCall(`/email_digest_sections/${id}`, options);
      // Invalidate cache after deletion
      invalidateApiCache("/email_digest_sections");
    },

    async handleSaveAlert(
      newRecipients: Array<RecipientObject>,
      removedRecipients: Array<string>,
    ) {
      const alertType = this.selectedAlert?.metadata?.alertType;
      const isCreatingAlert = this.unsavedAlertSelected;

      // 1. Update alert attributes
      const resp = await this.createOrUpdateAlert();
      if (
        resp?.error?.value?.data?.errors[0]?.detail ===
        "Name for an alert must be unique"
      ) {
        throw new Error("alert name must be unique");
      } else if (resp?.error?.value?.statusCode === 422) {
        throw new Error("plan limit exceeded");
      }

      if (isCreatingAlert) {
        if (alertType === "digest") {
          this.totalDigestAlertsCount += 1;
        } else {
          this.totalRealTimeAlertsCount += 1;
        }
      }

      // 2. Remove any recipients that have been marked for removal
      for (const recipientId of removedRecipients) {
        await this.removeRecipient(recipientId);
      }

      // 3. Save new recipient entries
      for (const recipient of newRecipients) {
        if (this.unsavedAlertSelected) {
          const key =
            this.selectedAlert?.metadata?.alertType === "digest"
              ? "email_digest"
              : "article_alert";

          if (this.selectedAlert && recipient.relationships[key]) {
            recipient.relationships[key].data.id = this.selectedAlert.id;
          }
        }
        await this.addNewRecipient(recipient);
      }

      let removeSectionPromises: Promise<void>[] = [];
      let updateSectionPromises: Promise<void>[] = [];

      if (this.selectedAlert?.metadata?.alertType === "digest") {
        // 4. Save new section entries sequentially (if applicable)
        for (const section of this.newDigestSections) {
          section.relationships.email_digest = {
            data: {
              id: this.selectedAlert?.id ?? "",
              type: "email-digests",
            },
          };
          await this.createOrUpdateDigestSection(section, "POST");
        }

        updateSectionPromises = this.updatedDigestSections?.map((section) => {
          section.relationships.email_digest = {
            data: {
              id: this.selectedAlert?.id ?? "",
              type: "email-digests",
            },
          };
          return this.createOrUpdateDigestSection(section, "PATCH");
        });

        // 5. Remove any sections that have been marked for removal (if applicable)
        removeSectionPromises = this.removedDigestSections.map((id) => {
          return this.removeOldDigestSection(id);
        });
      }

      // 6. Wait for all section operations to complete
      await Promise.all([...removeSectionPromises, ...updateSectionPromises]);

      // 7. Refetch the alert list
      if (this.selectedAlert?.metadata.alertType === "digest") {
        await this.fetchDigests();
      } else {
        await this.fetchAlerts();
      }
    },

    updateDigestSection(newDigestSection: DigestSectionItem) {
      this.digestSections = this.digestSections?.map((digestSection) => {
        return digestSection.id === newDigestSection.id
          ? newDigestSection
          : digestSection;
      });
    },

    async sendTestEmail(recipients: Array<string>) {
      const options: RequestOptions = {
        method: "POST",
        body: { data: { recipients } },
      };
      try {
        await useApiCall(
          `/email_digests/${this.selectedAlert?.id}/send_digest`,
          options,
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
  },
});
